import React, { useState, useEffect } from 'react';
import { Modal } from 'react-bootstrap';
import config from '../../config';
import ImageCropperModal from '../common/ImageCropperModal';

const AddEditCategoryModal = ({ show, onHide, onSave, category }) => {
    const [formData, setFormData] = useState({
        name: '',
        slug: '',
        description: ''
    });
    const [iconFile, setIconFile] = useState(null);
    const [iconPreview, setIconPreview] = useState(null);

    // Image Cropper State
    const [cropperState, setCropperState] = useState({
        show: false,
        imageSrc: null,
        fixedAspectRatio: 1, // 1:1 for category icon
        initialAspectRatio: 1,
        minWidth: 100 // Smaller min width for icons
    });

    useEffect(() => {
        if (category) {
            setFormData({
                name: category.name || '',
                slug: category.slug || '',
                description: category.description || ''
            });
            if (category.icon) {
                // Assuming icon is a path like /assets/... or a full URL
                const iconUrl = category.icon.startsWith('http') 
                    ? category.icon 
                    : `${config.API_BASE_URL}${category.icon}`;
                setIconPreview(iconUrl);
            } else {
                setIconPreview(null);
            }
        } else {
            setFormData({ name: '', slug: '', description: '' });
            setIconPreview(null);
        }
        setIconFile(null);
    }, [category]);

    const handleChange = (e) => {
        setFormData({ ...formData, [e.target.name]: e.target.value });
    };

    const handleFileChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            // Validate file size (10MB limit for source)
            if (file.size > 10 * 1024 * 1024) {
                alert("File size exceeds 10MB limit.");
                e.target.value = '';
                return;
            }
            
            const objectUrl = URL.createObjectURL(file);
            
            setCropperState({
                show: true,
                imageSrc: objectUrl,
                fixedAspectRatio: 1, // 1:1 for category icon
                initialAspectRatio: 1,
                minWidth: 100
            });
            
            // Reset input value
            e.target.value = '';
        }
    };

    const handleCropperHide = () => {
        if (cropperState.imageSrc) {
            URL.revokeObjectURL(cropperState.imageSrc);
        }
        setCropperState(prev => ({ ...prev, show: false, imageSrc: null }));
    };

    const handleCropComplete = (croppedFile, previewUrl) => {
        setIconFile(croppedFile);
        setIconPreview(previewUrl);
        handleCropperHide();
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        
        // Use FormData for file upload
        const data = new FormData();
        data.append('name', formData.name);
        data.append('slug', formData.slug);
        data.append('description', formData.description);
        if (iconFile) {
            data.append('icon', iconFile);
        }

        onSave(data);
    };

    return (
        <Modal show={show} onHide={onHide} centered className="modal-lg">
            <Modal.Header closeButton>
                <Modal.Title className="text-lg fw-semibold">
                    {category ? 'Edit Category' : 'Add New Category'}
                </Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <form onSubmit={handleSubmit}>
                    <div className="row gy-4">
                        <div className="col-12">
                            <label className="form-label fw-semibold text-primary-light text-sm mb-8">
                                Category Icon
                            </label>
                            <div className="d-flex align-items-center gap-3">
                                <div className="avatar-upload">
                                    <div className="avatar-preview w-80-px h-80-px rounded-circle overflow-hidden position-relative bg-neutral-50 border border-neutral-200 d-flex align-items-center justify-content-center">
                                        {iconPreview ? (
                                            <img src={iconPreview} alt="Icon Preview" className="w-100 h-100 object-fit-cover" />
                                        ) : (
                                            <span className="text-secondary-light text-2xl">📷</span>
                                        )}
                                    </div>
                                </div>
                                <div>
                                    <input 
                                        type="file" 
                                        className="form-control" 
                                        accept="image/png, image/jpeg, image/svg+xml, image/webp"
                                        onChange={handleFileChange}
                                    />
                                    <div className="form-text text-xs mt-1">Allowed: PNG, JPG, SVG, WEBP. Max 2MB.</div>
                                </div>
                            </div>
                        </div>
                        <div className="col-12">
                            <label className="form-label fw-semibold text-primary-light text-sm mb-8">
                                Category Name <span className="text-danger-600">*</span>
                            </label>
                            <input 
                                type="text" 
                                className="form-control radius-8" 
                                placeholder="Enter category name"
                                name="name"
                                value={formData.name}
                                onChange={handleChange}
                                required 
                            />
                        </div>
                        <div className="col-12">
                            <label className="form-label fw-semibold text-primary-light text-sm mb-8">
                                Slug (Optional)
                            </label>
                            <input 
                                type="text" 
                                className="form-control radius-8" 
                                placeholder="auto-generated-if-empty"
                                name="slug"
                                value={formData.slug}
                                onChange={handleChange}
                            />
                            <div className="form-text text-sm">Unique identifier for URLs. Leave empty to auto-generate from name.</div>
                        </div>
                        <div className="col-12">
                            <label className="form-label fw-semibold text-primary-light text-sm mb-8">
                                Description
                            </label>
                            <textarea 
                                className="form-control radius-8" 
                                placeholder="Enter category description"
                                name="description"
                                value={formData.description}
                                onChange={handleChange}
                                rows="3"
                            ></textarea>
                        </div>
                    </div>
                    <div className="d-flex align-items-center justify-content-end gap-3 mt-24">
                        <button type="button" className="btn btn-danger border-danger-600 text-danger-600 text-sm btn-sm px-24 py-12 radius-8" onClick={onHide}>
                            Cancel
                        </button>
                        <button type="submit" className="btn btn-primary text-sm btn-sm px-24 py-12 radius-8">
                            {category ? 'Update' : 'Save'}
                        </button>
                    </div>
                </form>
            </Modal.Body>
            
            {/* Image Cropper Modal */}
            {cropperState.show && (
                <ImageCropperModal
                    show={cropperState.show}
                    onHide={handleCropperHide}
                    imageSrc={cropperState.imageSrc}
                    onCropComplete={handleCropComplete}
                    fixedAspectRatio={cropperState.fixedAspectRatio}
                    initialAspectRatio={cropperState.initialAspectRatio}
                    minWidth={cropperState.minWidth}
                />
            )}
        </Modal>
    );
};

export default AddEditCategoryModal;
